import React, { useRef, useState, useEffect } from "react";
import {
  StyleSheet,
  View,
  TextInput as TextField,
  TouchableOpacity,
  ScrollView,
} from "react-native";
import Text from "@components/CustomText";
import { useDispatch, useSelector } from "react-redux";
import color from "@constants/color";
import { actions as authActions } from "@reducers/authReducer";
import CheckBox from "@react-native-community/checkbox";
import analytics from "@services/analytics";
import { AppEventsLogger } from "react-native-fbsdk-next";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
  IS_NEW_USER,
  SHOW_IJ_FLOW,
  SHOW_WALK_THROUGH,
} from "@constants/strings";
import useDebounce from "@hooks/useDebounce";
import t from "@constants/localization";
import testIds from "@constants/testIds";
import { whatsAppOpt, checkClCode, registerNewNumber } from "@services/api";
import { SIGNUP_FLOW_EVENTS } from "../../constants/analyticsEventsCT";
import { scale } from "../../utils/scale";
import Warning from "../../assets/svg/Warning";
import CrossIcon from "../../assets/svg/CrossCircle";
import PrimaryButton from "../../components/PrimaryButton";
import CityMallLogoBlack from "../../assets/svg/CityMallLogoBlack";
import { actions as configActions } from "@reducers/configReducer";

const PATH = "screens/Auth/EnterName";
const COMPLETE_REGISTRATION = "fb_mobile_complete_registration";

const containsSpecialCharsAndDigits = (name) => {
  const specialChars = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
  const hasNumber = /\d+/g;
  return specialChars.test(name) || hasNumber.test(name);
};

const EnterName = (props) => {
  const { route: { params: { phoneNumber = "" } = {} } = {}, navigation } =
    props;
  const [name, setName] = useState("");
  const [checked, setChecked] = useState(false);
  const [code, setCode] = useState("");
  const [isReferCodeWrong, setIsReferCodeWrong] = useState(null);
  const [isNameWrong, setIsNameWrong] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [optInChecked, setOptInChecked] = useState(true);
  const debouncedName = useDebounce(name);

  const { phone_number } = useSelector(({ auth: { user } }) => user);
  const { SHOW_SIGNUP_CODE } = useSelector(
    (state) => state.configReducer.appSettings,
  );

  const leaderPhoneNo = phoneNumber || phone_number;
  const nameFieldRef = useRef();
  const dispatch = useDispatch();

  useEffect(() => {
    if (containsSpecialCharsAndDigits(name)) {
      setIsNameWrong(true);
    } else {
      setIsNameWrong(false);
    }
  }, [name]);

  const onChangeText = (text) => {
    // eslint-disable-next-line no-control-regex
    const formattedText = text.replace(/[^\x00-\x7F]/, "");
    setName(formattedText);
    return formattedText;
  };

  const nameInputBlur = () => {
    debouncedName?.trim().length > 2 &&
      analytics.trackEvent(SIGNUP_FLOW_EVENTS.NAME_ENTERED, {
        name,
      });
  };

  const checkAllFields = () => {
    if (isNameWrong) {
      return true;
    }
    if (isReferCodeWrong) {
      return true;
    }
    if (name.trim().length < 2) {
      return true;
    }
    if (checked && code.length === 0) {
      return true;
    }
    return false;
  };

  const onSubmit = () => {
    setIsSubmitting(true);
    if (!leaderPhoneNo || !name) return;
    const testSpecialCharRegexp = /^[a-zA-Z\s]+$/;
    const testNumberRegexp = /^[a-zA-Z\s]+$/;
    if (!testSpecialCharRegexp.test(name) || !testNumberRegexp.test()) {
      setIsSubmitting(false);
      return;
    }
    registerNewNumber(leaderPhoneNo, name.trim(), __DEV__, code)
      .then(() => {
        dispatch(authActions.setUserNumberForLogin(phoneNumber));
        // call restoreusertoken
        analytics.trackEvent(SIGNUP_FLOW_EVENTS.NAME_VERIFIED, { name });
        AppEventsLogger.logEvent(COMPLETE_REGISTRATION, {
          currency: "INR",
        });
        whatsAppOpt(optInChecked);
        analytics.trackEvent(SIGNUP_FLOW_EVENTS.SUCCESSFULL_REGISTRATION, {
          first_time: true,
        });
        AsyncStorage.setItem(IS_NEW_USER, "true");
        AsyncStorage.setItem(SHOW_IJ_FLOW, "true");
        AsyncStorage.setItem(SHOW_WALK_THROUGH, "true");
        dispatch(configActions.syncUser());
      })
      .catch((e) => {
        setIsSubmitting(false);
        console.log(e.response);
      });
  };

  const onCheckRefCode = () => {
    if (checked) {
      code
        ? checkClCode(code)
            .then((refRes) => {
              if (refRes?.data?.success) {
                analytics.trackEvent(
                  SIGNUP_FLOW_EVENTS.REFERRAL_CODE_SUBMITTED,
                  {
                    referral_code: code,
                  },
                );
                setIsReferCodeWrong(false);
                onSubmit();
              } else {
                setIsReferCodeWrong(true);
              }
            })
            .catch((e) => {
              setIsReferCodeWrong(true);
            })
        : setIsReferCodeWrong(true);
    } else {
      onSubmit();
    }
  };

  const handleCodeCrossClick = () => {
    setCode("");
    setIsReferCodeWrong(null);
  };

  const handleCodeChange = (code) => {
    setCode(code);
    setIsReferCodeWrong(null);
  };

  const changeSignupCodeBox = (newValue) => {
    setChecked(newValue);
    setCode("");
    setIsReferCodeWrong(false);
  };

  return (
    <ScrollView keyboardShouldPersistTaps="handled" style={styles.container}>
      <View style={{ flex: 1 }}>
        <View style={styles.header}>
          <CityMallLogoBlack height={30} width={114} />
        </View>
        <View style={styles.title}>
          <Text variant="title_20" style={styles.titleText}>
            {t(`${PATH}/enterName`)}
          </Text>
        </View>
        <View style={styles.nameInputBox}>
          <Text variant="caption_2" style={styles.inputLable}>
            {name.length > 0 ? t(`${PATH}/name`) : ""}
          </Text>
          <TextField
            testID={testIds.Enter_Name_Field}
            label={t(`${PATH}/name`)}
            keyboardType="default"
            onChangeText={onChangeText}
            value={name}
            ref={nameFieldRef}
            style={[styles.nameInputField, { paddingBottom: 4 }]}
            placeholder={t(`${PATH}/name`)}
            labelFontSize={14}
            tintColor="rgba(0, 0, 0, .38)"
            disabledLineWidth={0}
            activeLineWidth={0}
            maxLength={36}
            autoCompleteType="name"
            spellCheck={false}
            autoCorrect={false}
            onBlur={nameInputBlur}
          />
          <View style={styles.space} />
        </View>

        {isNameWrong && name.length > 2 && (
          <Text
            style={{
              color: color.plain_red,
              paddingHorizontal: 20,
              fontSize: 12,
            }}
          >
            {t(`${PATH}/nameCheck`)}
          </Text>
        )}
        {SHOW_SIGNUP_CODE && (
          <View style={styles.signupCodeTopBox}>
            <View style={[styles.signupCodeInnerBox, { alignSelf: "center" }]}>
              <CheckBox
                style={styles.signupCodeCheckBox}
                value={checked}
                tintColors={{ true: "rgba(0, 0, 0, 0.8)" }}
                onValueChange={changeSignupCodeBox}
              />
              <Text style={styles.signUpCodeText}>
                {t(`${PATH}/iHaveReferralCode`)}
              </Text>
            </View>
            <View style={{ flex: 1 }}>
              {checked && (
                <View
                  style={{
                    position: "relative",
                  }}
                >
                  <TextField
                    autoFocus={true}
                    fontType="head2"
                    placeholder={t(`${PATH}/enterCouponCode`)}
                    value={code}
                    onChangeText={handleCodeChange}
                    autoCapitalize="characters"
                    style={[
                      styles.signUpCodeInput,
                      {
                        borderBottomColor: isReferCodeWrong
                          ? "#FF2E00"
                          : "rgba(0, 0, 0, 0.2)",
                      },
                    ]}
                  />
                  {code.length > 0 && (
                    <TouchableOpacity
                      onPress={handleCodeCrossClick}
                      style={[
                        {
                          position: "absolute",
                          right: 8,
                          top: 4,
                        },
                        { opacity: 0.86 },
                      ]}
                    >
                      <CrossIcon fill="black" height={18} width={18} />
                    </TouchableOpacity>
                  )}
                </View>
              )}
              {isReferCodeWrong && (
                <View style={[styles.referCodeWrongBox, { marginTop: 2 }]}>
                  <Warning color="#ff2e00" style={{ marginRight: 6 }} />
                  <Text style={styles.checkCouponCodeText}>
                    {t(`${PATH}/checkCouponCode`)}
                  </Text>
                </View>
              )}
            </View>
          </View>
        )}
        <View testID={testIds.Enter_Name} style={styles.submitButton}>
          <PrimaryButton
            loading={isSubmitting}
            disabled={checkAllFields()}
            buttonStyle={{ marginHorizontal: scale(20) }}
            onPress={onCheckRefCode}
            text={t(`${PATH}/goAhead`)}
          />
        </View>
      </View>

      <View style={styles.WACheckbox}>
        <CheckBox
          value={optInChecked}
          tintColors={{ true: color.primary }}
          onValueChange={setOptInChecked}
        />
        <Text style={styles.WACheckboxText}>
          {t(`${PATH}/subscribeForWhatsappUpdates`)}
        </Text>
      </View>
    </ScrollView>
  );
};

export default EnterName;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: color.white,
  },
  header: {
    padding: 20,
    paddingLeft: 15,
    marginBottom: 40,
  },
  title: {
    paddingHorizontal: 20,
    color: color.lightBlack,
  },
  nameInputBox: {
    paddingHorizontal: 20,
    paddingTop: 20,
    position: "relative",
  },
  space: {
    flexDirection: "row",
    marginTop: 10,
    alignItems: "center",
  },
  inputLable: {
    color: "rgba(0, 0, 0, 0.36)",
  },
  nameInputField: {
    fontFamily: "NotoSansDevanagari-SemiBold",
    paddingHorizontal: 0,
    paddingTop: 0,
    fontSize: 16,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(0, 0, 0, 0.2)",
  },
  signupCodeTopBox: {
    paddingHorizontal: 20,
    flexDirection: "row",
  },
  signupCodeInnerBox: {
    flexDirection: "row",
    marginLeft: -6,
    flex: 1,
  },
  signupCodeCheckBox: {
    margin: 0,
    marginHorizontal: 0,
    paddingHorizontal: 0,
    padding: 0,
  },
  signUpCodeText: {
    fontSize: scale(13),
    lineHeight: scale(20),
    color: "rgba(0, 0, 0, 0.56)",
    marginTop: 8,
  },
  signUpCodeTextInputLable: {
    color: "rgba(0, 0, 0, 0.36)",
    marginBottom: 0,
  },
  signUpCodeInput: {
    fontFamily: "NotoSansDevanagari-SemiBold",
    fontSize: scale(14),
    padding: 0,
    paddingVertical: 0,
    marginVertical: 0,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(0, 0, 0, 0.2)",
    paddingHorizontal: 0,
  },
  referCodeWrongBox: {
    flexDirection: "row",
    alignItems: "center",
  },
  checkCouponCodeText: {
    color: "#ff2e00",
    fontSize: scale(10),
  },
  submitButton: {
    flexDirection: "row",
    marginTop: 40,
    alignItems: "center",
  },
  WACheckbox: {
    paddingHorizontal: 20,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: color.white,
    marginTop: scale(15),
    marginLeft: -6,
  },
  WACheckboxText: {
    fontSize: scale(14),
    lineHeight: scale(20),
    color: "rgb(0, 0, 0)",
  },
});
